<script setup lang="ts">
import type { KjdsDetailPageProps } from '@/types/appTypes'

const props = withDefaults(defineProps<KjdsDetailPageProps>(), {})

const app = useNuxtApp()
const kijidasConfig = useAppConfig().kijidas
const storeArticleList = useStoreArticleList()
const storeArticleDetail = useStoreArticleDetail()
const storeIndex = useStoreIndex()

const baseTitle = 'ニュース'
let headTitle = ''
let headDescription = ''

const refData = reactive({
  aTag: null, // タグリスト
  aEventItems: null,
  reportItems: null,
  reportObjects: [] as ArticleObject[],
  specialItems: null,
  jyunkaiItems: null,
})

const setHead = () => {
  const articleTitle = props.kjdsDetailData.article.title
  headTitle = `${articleTitle} | ${baseTitle}`
  headDescription = `${articleTitle}のニュースページ。　実際に現場で取材したニュースを、美しい写真とともにお送りします。美術館・博物館・アートのニュースはアイエムで！`
  if (props.kjdsDetailData.article.description?.length) {
    headDescription = props.kjdsDetailData.article.description
  }
  // ぱんくず作成
  storeIndex.setBreadCrumbs([
    {
      text: baseTitle,
      disabled: false,
      to: { path: '/news' },
    },
    {
      text: articleTitle,
      disabled: true,
    },
  ])

  // og:imageはeye_catch_image_objectがあるときだけ
  const ogImage = storeIndex.isArticleEyeCatchImageURL(props.kjdsDetailData.article.eye_catch_image_object)
    ? storeIndex.getArticleEyeCatchImageURL(props.kjdsDetailData.article.eye_catch_image_object)
    : undefined

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
    ogImage,
  })
  useHead(headInput)
}

const initPage = async () => {
  if (!refData.aTag) {
    refData.aTag = app.$getArticleTags(props.kjdsDetailData.article)
  }

  // ニュースに紐づく展覧会を取得
  if (!refData.aEventItems) {
    refData.aEventItems = app.$getArticleRelationsMulti(
      props.kjdsDetailData.article,
      'event',
      null,
    )
  }

  const reportMap = new Map<string, Article>()
  const specialMap = new Map<string, Article>()
  const jyunkaiMap = new Map<string, Article>()

  const results = await Promise.all(
    (refData.aEventItems ?? []).map(eventItem =>
      storeArticleDetail.fetchDetail({
        fetchParams: {
          articleTypeSlug: 'event',
          articleSlugOrId: eventItem.article_slug,
        },
        isNoThrowError: true,
      }),
    ),
  )

  // 取得した展覧会詳細に紐づく特集/巡回展を取得
  for (const res of results) {
    const eventDetail = res?.data?.article

    const report = app.$getArticleRelationsMulti(eventDetail, 'report', null)
    report.forEach(r => reportMap.set(r.article_slug, r))

    const special = app.$getArticleRelationsMulti(eventDetail, 'special', null)
    special.forEach(p => specialMap.set(p.article_slug, p))

    const jyunkai = app.$getArticleRelationsMulti(eventDetail, 'jyunkai', null)
    jyunkai.forEach(j => jyunkaiMap.set(j.article_slug, j))

    refData.reportObjects.push(...(res?.data?.article_objects ?? []))
  }

  refData.reportItems = app.$sortArticleOpenDate([...reportMap.values()], true)
  refData.specialItems = app.$sortArticleOpenDate([...specialMap.values()], true)
  refData.jyunkaiItems = app.$sortArticleOpenDate([...jyunkaiMap.values()], true)
}
await initPage()
setHead()
</script>

<template>
  <div class="p-articleDetailPage">
    <NewsBody
      v-bind:title="headTitle"
      v-bind:item="props.kjdsDetailData.article"
    />

    <ArticleTagList
      v-if="refData.aTag?.length > 0"
      v-bind:article-name="props.kjdsDetailData.article.article_type_slug"
      v-bind:items="refData.aTag"
      class="pc-only"
    />

    <RelationBox
      article="present"
      v-bind:header="'関連するプレゼント'"
      v-bind:relations="props.kjdsDetailData.article.relations"
    />
    <RelationBox
      article="event"
      v-bind:header="'関連する展覧会'"
      v-bind:relations="props.kjdsDetailData.article.relations"
    />
    <RelationBox
      article="report"
      v-bind:header="'関連するレポート'"
      v-bind:articles="refData.reportItems"
      v-bind:objects="refData.reportObjects"
    />
    <RelationBox
      article="museum"
      v-bind:header="'関連するミュージアム'"
      v-bind:relations="props.kjdsDetailData.article.relations"
    />

    <RelationBox
      v-if="refData.specialItems.length > 0"
      article="special"
      v-bind:header="'関連する特集'"
      v-bind:articles="refData.specialItems"
    />

    <RelationBox
      v-if="refData.jyunkaiItems.length > 0"
      article="jyunkai"
      v-bind:header="'関連する巡回展'"
      v-bind:articles="refData.jyunkaiItems"
    />

    <ArticleTagList
      v-if="refData.aTag?.length > 0"
      v-bind:article-name="props.kjdsDetailData.article.article_type_slug"
      v-bind:items="refData.aTag"
      class="sp-only"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.c-news {
  margin-top: 24px;
}

.c-addNewsTitle {
  margin-top: 105px;
  color: var(--color_text_base);
  font-family: ver(--g_font_family_SawarabiGothic);
  font-size: 20px;
  font-weight: 500;

  @include responsive.mq_down(sp) {
    margin-top: 85px;
    font-size: 18px;
  }
}

.c-addNewsList {
  margin-top: 15px;
  margin-bottom: 80px;
}

.c-reportRelated {
  margin-top: 110px;
}
</style>
